/**
 * themeSearch.js — Moteur de recherche thématique 100% hors-ligne
 * Utilise sql.js (WebAssembly SQLite) pour interroger index_fts (FTS5)
 * dans bible_mobile_finale.db.
 */
import initSqlJs from "sql.js";

const DB_URL = "/bible_mobile_finale.db";
const IDB_NAME = "bible-fillion-fts";
const IDB_STORE = "db-files";
const IDB_KEY = "bible_mobile_finale_v5";

let dbInstance = null;
let initPromise = null;

// ──────────────────── IndexedDB helpers ────────────────────

function openIDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function loadFromIDB() {
  try {
    const db = await openIDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, "readonly");
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(IDB_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function saveToIDB(buffer) {
  try {
    const db = await openIDB();
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).put(buffer, IDB_KEY);
  } catch (e) {
    console.warn("[themeSearch] Could not cache DB in IndexedDB:", e);
  }
}

// ──────────────────── SQL.js initialisation ────────────────────

async function initDB(onProgress) {
  if (dbInstance) return dbInstance;

  // Prevent multiple concurrent initialisations
  if (initPromise) return initPromise;

  initPromise = (async () => {
    if (onProgress) onProgress("Initialisation du moteur de recherche…");

    // 1. Load sql.js WASM
    const SQL = await initSqlJs({
      locateFile: (file) =>
        `https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.14.2/${file}`,
    });

    // 2. Try IndexedDB cache first (offline-capable)
    let buffer = await loadFromIDB();

    if (!buffer) {
      if (onProgress) onProgress("Téléchargement de l'index thématique…");
      const response = await fetch(DB_URL);
      if (!response.ok) throw new Error(`Failed to fetch DB: ${response.status}`);
      buffer = await response.arrayBuffer();
      await saveToIDB(buffer);
    }

    if (onProgress) onProgress("Préparation des données…");
    dbInstance = new SQL.Database(new Uint8Array(buffer));
    return dbInstance;
  })();

  try {
    const result = await initPromise;
    return result;
  } finally {
    initPromise = null;
  }
}

// ──────────────────── Public API ────────────────────

/**
 * Search the FTS5 index by theme.
 * @param {string} query — Free-text user input
 * @param {number} limit — Max results (default 10)
 * @param {function} onProgress — Optional progress callback
 * @returns {Promise<Array<{source, theme, reference, text, snippet}>>}
 */
export async function searchByTheme(query, limit = 10, onProgress = null, sourceFilter = 'FILLION') {
  if (!query || query.trim().length < 2) return [];

  const db = await initDB(onProgress);

  // Build FTS5 query: each word as a prefix token for partial matching
  const words = query
    .trim()
    .split(/\s+/)
    .filter((w) => w.length >= 2)
    .map((w) => `"${w.replace(/"/g, "")}"*`);

  if (words.length === 0) return [];

  const ftsQuery = words.join(" ");

  try {
    const stmt = db.prepare(
      `SELECT source, libelle_theme, reference_verset, 
              substr(texte, 1, 250) AS snippet, book_id, chapter_number, verse_number
       FROM index_fts
       WHERE index_fts MATCH ? AND source = ?
       LIMIT ?`
    );
    stmt.bind([ftsQuery, sourceFilter, limit]);

    const results = [];
    while (stmt.step()) {
      const row = stmt.getAsObject();
      results.push({
        source: row.source,
        theme: row.libelle_theme,
        reference: row.reference_verset,
        snippet: row.snippet || "",
        book_id: row.book_id,
        chapter: row.chapter_number,
        verse: row.verse_number,
      });
    }
    stmt.free();
    return results;
  } catch (e) {
    console.error("[themeSearch] FTS5 query error:", e);
    // Fallback: try simpler query without prefix
    try {
      const simpleQuery = query.trim().replace(/"/g, "");
      const stmt = db.prepare(
        `SELECT source, libelle_theme, reference_verset, 
                substr(texte, 1, 250) AS snippet, book_id, chapter_number, verse_number
         FROM index_fts
         WHERE index_fts MATCH ? AND source = ?
         LIMIT ?`
      );
      stmt.bind([`"${simpleQuery}"`, sourceFilter, limit]);
      const results = [];
      while (stmt.step()) {
        const row = stmt.getAsObject();
        results.push({
          source: row.source,
          theme: row.libelle_theme,
          reference: row.reference_verset,
          snippet: row.snippet || "",
          book_id: row.book_id,
          chapter: row.chapter_number,
          verse: row.verse_number,
        });
      }
      stmt.free();
      return results;
    } catch (e2) {
      console.error("[themeSearch] Fallback query also failed:", e2);
      return [];
    }
  }
}

/**
 * Check if the FTS database is already cached (ready for offline).
 */
export async function isDBCached() {
  const cached = await loadFromIDB();
  return cached !== null;
}
